/* ═══════════════════════════════════════════════════════════════════════════
   HexClean — motion system
   ───────────────────────────────────────────────────────────────────────────
   One place that answers "how fast, how bouncy, from where". Components import
   a named spring; they do not invent numbers. A codebase where every component
   picks its own duration is a codebase where nothing feels like one product.

   Two parameters, not three. Apple deliberately replaced mass/stiffness/damping
   with `response` (how quickly the value reaches the target, in seconds) and
   `damping ratio` (how much it overshoots). Motion's spring takes `duration` +
   `bounce`, which is the same two-axis idea under different names:

       response  ≈ duration
       damping   ≈ 1 - bounce      (damping 1.0 → bounce 0, critically damped)

   The house default is critically damped. Bounce is earned, not decorative: it
   is applied only where the *gesture itself carried momentum* — a flick, a
   throw, a drag release. Overshoot on a menu that merely faded in feels wrong;
   overshoot on a card you threw feels right.
   ═══════════════════════════════════════════════════════════════════════════ */

import type { Transition, Variants } from 'motion/react'

/* ── springs ──────────────────────────────────────────────────────────────── */

/** Default UI motion. Critically damped — arrives and stops, no wobble. */
export const springUI: Transition = { type: 'spring', duration: 0.4, bounce: 0 }

/** Small, frequent state changes: chips, pills, badge counts. Snappier. */
export const springSnap: Transition = { type: 'spring', duration: 0.28, bounce: 0 }

/** Sheets and drawers. Apple ships damping 0.8 / response 0.3 here — the drag
 *  that opens them carries momentum, so a trace of overshoot is correct. */
export const springSheet: Transition = { type: 'spring', duration: 0.34, bounce: 0.18 }

/** Release of a drag or flick. The only place a real bounce is warranted. */
export const springFlick: Transition = { type: 'spring', duration: 0.42, bounce: 0.24 }

/** Repositioning something already on screen (a shared layout pill, a
 *  reordering row). Apple: damping 1.0 / response 0.4. */
export const springMove: Transition = { type: 'spring', duration: 0.4, bounce: 0 }

/* ── tweens ───────────────────────────────────────────────────────────────────
   Springs are for anything a user can touch. Fixed-duration tweens are for
   opacity and colour, where there is no physical object to have momentum.
   Entering and exiting always use ease-out: it starts fast, so the interface
   answers on the frame the user is watching most closely. `ease-in` is never
   correct for UI — it delays the first frame, which is exactly the wrong one
   to delay. */

export const EASE_OUT = [0.23, 1, 0.32, 1] as const
export const EASE_IN_OUT = [0.77, 0, 0.175, 1] as const
export const EASE_DRAWER = [0.32, 0.72, 0, 1] as const

export const fade: Transition = { duration: 0.18, ease: EASE_OUT }
export const fadeSlow: Transition = { duration: 0.28, ease: EASE_OUT }

/** Exits are faster than entrances. The user has already decided; the system
 *  is only getting out of the way, and lingering reads as lag. */
export const fadeExit: Transition = { duration: 0.12, ease: EASE_OUT }

/* ── variants ─────────────────────────────────────────────────────────────────
   Nothing in the real world appears from nothing, so nothing here starts at
   scale(0) or at a translate large enough to look like it flew in from off
   screen. 8–12px and 0.96 are enough to read as "arrived" without the motion
   itself becoming the message. */

export const revealUp: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.36, ease: EASE_OUT } },
}

export const revealIn: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: EASE_OUT } },
}

/** Parent of a staggered group. Delays are short by design: 30–80ms reads as
 *  cascade, 150ms reads as the page being slow. */
export const stagger = (each = 0.045, delay = 0): Variants => ({
  hidden: {},
  show: { transition: { staggerChildren: each, delayChildren: delay } },
})

/** Popovers and menus scale from their trigger, never from their own centre —
 *  `transform-origin` is set by the caller from the trigger's position. Modals
 *  are the exception and stay centred, because they are not anchored to
 *  anything. */
export const popover: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: -4 },
  show: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.16, ease: EASE_OUT } },
  exit: { opacity: 0, scale: 0.97, y: -2, transition: { duration: 0.1, ease: EASE_OUT } },
}

/** Glass surfaces materialise rather than fade: blur and scale move together
 *  so the surface reads as a real material arriving, not a PNG turning on. */
export const materialize: Variants = {
  hidden: { opacity: 0, scale: 0.985, filter: 'blur(8px)' },
  show: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: 0.34, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    scale: 0.99,
    filter: 'blur(4px)',
    transition: { duration: 0.14, ease: EASE_OUT },
  },
}

/* ── ambient motion ───────────────────────────────────────────────────────────
   Everything above is *reactive*: it runs because the user did something.
   These run on their own, which makes them a different kind of decision and
   the easiest kind to get wrong.

   The rule they follow: ambient motion must be slow enough that you cannot
   watch it happen. A float with a 3s period reads as a bobbing UI element and
   pulls the eye off the copy; the same float at 7s reads as the page being
   alive. If you can time it by looking at it, it is too fast.

   They are also confined to the landing page. A marketing page is seen once
   and its job is to hold attention; a screen someone uses twice a day must
   hold still. */

/** Continuous drift for the hero's floating cards. Long, offset per card. */
export const FLOAT_SECONDS = 7

/** Ken Burns on a hero photograph. Half a minute for an 8% scale — the motion
 *  should register as depth, never as a zoom you can see moving. */
export const KENBURNS_SECONDS = 30

/** How far a parallax layer travels across its whole scroll pass, in px.
 *  Beyond ~60 the layers stop reading as depth and start reading as lag. */
export const PARALLAX_RANGE = 48

/** Reveal with the blur clearing as it arrives. Reserved for the landing page:
 *  it costs a filter animation, which is the expensive kind, so it is worth it
 *  once per section and never in a list. */
export const revealSoft: Variants = {
  hidden: { opacity: 0, y: 18, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.55, ease: EASE_OUT },
  },
}

/* ── physics helpers ──────────────────────────────────────────────────────── */

/**
 * Where a flick would come to rest if it decelerated like a native scroller.
 *
 * This is Apple's projection function from the *Designing Fluid Interfaces*
 * sample code, not the physics-textbook `v²/(2a)` — the exponential-decay form
 * is what iOS actually ships and what UIScrollView, Vaul and Embla all match.
 *
 * Snapping to the nearest stop measured from the *release point* is what makes
 * a carousel feel sticky; snapping to the nearest stop measured from the
 * projected point is what makes a flick feel thrown.
 *
 * @param velocity px/s at release
 * @param decelerationRate 0.998 ≈ normal scroll feel, 0.99 ≈ snappier
 * @returns the signed distance the element would still travel
 */
export function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate)
}

/**
 * Progressive resistance past a boundary. Real things slow before they stop; a
 * hard clamp reads as frozen, which the user interprets as "the app hung"
 * rather than "there is nothing more here".
 *
 * @param overshoot how far past the bound the pointer has travelled
 * @param dimension the size of the dragged surface, which sets the scale of
 *        the resistance so a small sheet and a full page resist alike
 */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot))
}

/** Nearest value in `stops` to `n`. Used with `project()`, never alone. */
export function nearest(n: number, stops: readonly number[]): number {
  let best = stops[0] ?? n
  let bestDist = Math.abs(n - best)
  for (const s of stops) {
    const d = Math.abs(n - s)
    if (d < bestDist) {
      best = s
      bestDist = d
    }
  }
  return best
}

/**
 * Velocity from a short sample history rather than the last two points. A
 * single frame pair is dominated by jitter at the moment the finger lifts —
 * which is precisely the sample that decides whether a swipe dismisses.
 */
export interface Sample {
  v: number
  t: number
}

export function velocityFrom(samples: readonly Sample[], window = 100): number {
  if (samples.length < 2) return 0
  const last = samples[samples.length - 1]
  if (!last) return 0
  let first = samples[0]!
  for (let i = samples.length - 1; i >= 0; i--) {
    const s = samples[i]!
    if (last.t - s.t > window) break
    first = s
  }
  const dt = last.t - first.t
  if (dt <= 0) return 0
  return ((last.v - first.v) / dt) * 1000 // px/s
}

/**
 * Should a drag commit, given both how far it went and how fast it was moving?
 *
 * Distance alone forces the user to drag the whole way; velocity alone fires on
 * an accidental twitch. A quick flick should be enough, so either condition
 * commits — the threshold pair is the same shape Sonner uses for swipe-to-
 * dismiss, tuned here per surface via `distance`.
 */
export function shouldCommit(
  travelled: number,
  velocity: number,
  distance: number,
  minVelocity = 380,
): boolean {
  return Math.abs(travelled) >= distance || Math.abs(velocity) >= minVelocity
}
